/**
 * WebContainer + Vite preview engine.
 *
 * Replaces the esbuild in-browser bundler with a real Vite dev server
 * running inside a WebContainer. This handles imports, Tailwind, HMR,
 * CSS modules, and everything else Vite supports — no more stub components.
 */

import { WebContainer, type FileSystemTree } from '@webcontainer/api';
import type { BundleOptions } from './esbuildBundler';
import { SHADCN_TAILWIND_DIRECTIVES } from '../helpers/shadcnBundle';
import { PREVIEW_INSPECTOR_SCRIPT } from './previewInspectorScript';

// ── Singleton WebContainer instance ──
let _container: WebContainer | null = null;
let _booting: Promise<WebContainer> | null = null;
let _serverUrl: string | null = null;
let _currentSessionId: string | null = null;
let _installedDepsHash: string | null = null; // Hash of last-installed package.json deps
let _hasBootstrapped = false; // Whether any session has been mounted before
let _startingPreview: Promise<WebContainerPreviewResult> | null = null; // Lock to prevent concurrent starts
let _devProcess: { kill: () => void } | null = null; // Track current Vite process for cleanup

/** Get or boot the shared WebContainer instance. */
async function getContainer(): Promise<WebContainer> {
  if (_container) return _container;
  if (_booting) return _booting;

  if (typeof window !== 'undefined' && !window.crossOriginIsolated) {
    throw new Error(
      'Page is not cross-origin isolated. WebContainer requires COOP/COEP headers.',
    );
  }

  _booting = WebContainer.boot()
    .then((wc) => {
      _container = wc;
      _booting = null;
      return wc;
    })
    .catch((err) => {
      _booting = null;
      throw err;
    });
  return _booting;
}

/**
 * Deduplicate import statements in JS/TS/JSX/TSX code.
 * Removes: self-imports (About.tsx importing "./About"), duplicate default imports
 * (same identifier from different paths), and exact-duplicate import lines.
 */
function stripConflictingStubs(code: string): string {
  // Collect all imported identifiers (named and default)
  const importedNames = new Set<string>();
  const importDefaultRe = /^import\s+(\w+)\s+from\s+/gm;
  const importNamedRe = /^import\s+(?:type\s+)?\{([^}]+)\}\s+from\s+/gm;
  let m: RegExpExecArray | null;
  while ((m = importDefaultRe.exec(code)) !== null) {
    importedNames.add(m[1]);
  }
  while ((m = importNamedRe.exec(code)) !== null) {
    for (const name of m[1].split(',')) {
      const alias = name.trim().split(/\s+as\s+/).pop()?.trim();
      if (alias) importedNames.add(alias);
    }
  }
  if (importedNames.size === 0) return code;

  // Remove "// Auto-generated stubs for missing components" comment lines
  // and any const XYZ: React.FC<any> stubs that shadow imported names
  const lines = code.split('\n');
  const result: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    // Skip the comment marker
    if (trimmed === '// Auto-generated stubs for missing components') continue;
    // Skip stub declarations like: const Canvas: React.FC<any> = (props) => ...
    const stubMatch = trimmed.match(/^const\s+(\w+)\s*:\s*React\.FC<any>\s*=/);
    if (stubMatch && importedNames.has(stubMatch[1])) continue;
    result.push(line);
  }
  return result.join('\n');
}

function deduplicateImports(code: string, fileName: string): string {
  const lines = code.split('\n');
  const seenImportLines = new Set<string>();
  const seenDefaultNames = new Set<string>();
  const result: string[] = [];

  // Derive the module's own base name for self-import detection (e.g., "About.tsx" → "About")
  const baseName = fileName.replace(/^.*\//, '').replace(/\.(tsx?|jsx?|mjs|cjs)$/, '');

  // Detect the component's own exported name to prevent imports that shadow it.
  // Matches: export default function Foo, export default class Foo, const Foo = ...export default Foo
  const exportNameMatch = code.match(
    /export\s+default\s+(?:function|class)\s+(\w+)/,
  );
  const exportedName = exportNameMatch?.[1] || baseName;

  for (const line of lines) {
    const trimmed = line.trim();

    // Check if this is an import line
    const importMatch = trimmed.match(
      /^import\s+(?:(?:type\s+)?(?:\{[^}]*\}|\*\s+as\s+\w+|\w+)(?:\s*,\s*(?:\{[^}]*\}|\*\s+as\s+\w+))?\s+from\s+)?['"]([^'"]+)['"]\s*;?\s*$/,
    );

    if (importMatch) {
      const importPath = importMatch[1];

      // Self-import detection: About.tsx importing "./About" or "../pages/About"
      if (baseName && baseName !== 'index') {
        const importBaseName = importPath.replace(/^.*\//, '').replace(/\.(tsx?|jsx?|mjs|cjs)$/, '');
        if (importBaseName === baseName) continue;
      }

      // Exact line dedup
      const normalized = trimmed.replace(/\s+/g, ' ').replace(/;$/, '');
      if (seenImportLines.has(normalized)) continue;
      seenImportLines.add(normalized);

      // Default import name dedup (e.g., "import Features from './pages/Features'" vs "import Features from './Features'")
      const defaultMatch = trimmed.match(/^import\s+(\w+)\s+from\s+/);
      if (defaultMatch) {
        const name = defaultMatch[1];
        if (seenDefaultNames.has(name)) continue;
        seenDefaultNames.add(name);

        // Drop default imports that shadow the file's own exported component name
        // e.g., Footer.tsx with "import Footer from './layout/Footer'" — shadows export default function Footer
        if (exportedName && name === exportedName) continue;
      }

      // Drop named imports that shadow the file's own exported name
      // e.g., Footer.tsx with "import { Footer } from '...'" — shadows export default function Footer
      if (exportedName) {
        const namedMatch = trimmed.match(/^import\s+\{([^}]+)\}\s+from\s+/);
        if (namedMatch) {
          const names = namedMatch[1].split(',').map((n) => n.trim().split(/\s+as\s+/).pop()!.trim());
          if (names.includes(exportedName)) {
            // Remove just the conflicting name from the import; if it's the only one, drop the whole line
            const filtered = namedMatch[1]
              .split(',')
              .filter((n) => {
                const alias = n.trim().split(/\s+as\s+/).pop()!.trim();
                return alias !== exportedName;
              })
              .join(', ');
            if (!filtered.trim()) continue; // whole import was just the conflicting name
            // Rebuild import with remaining names
            const fromPart = trimmed.match(/from\s+['"][^'"]+['"]\s*;?\s*$/);
            if (fromPart) {
              result.push(`import { ${filtered.trim()} } ${fromPart[0]}`);
              continue;
            }
          }
        }
      }
    }

    result.push(line);
  }

  return result.join('\n');
}

/** Convert session files into WebContainer FileSystemTree format. */
/**
 * Append matching closers for any unclosed raw-text HTML elements
 * (<script>, <style>, <title>, <textarea>). Vite's parse5 parser throws
 * `eof-in-element-that-can-contain-only-text` and the dev server returns 500
 * when index.html has these — so we self-heal by counting openers vs closers
 * and appending the missing closers at the end of the document.
 */
function sanitizeRawTextElements(html: string, source: string): string {
  let out = html;
  for (const tag of ['script', 'style', 'title', 'textarea'] as const) {
    // Only count actual opener tags, skip self-closed `<script src="..." />`
    // (rare but valid in older HTML). Also ignore closers that appear inside
    // attribute values — practical match: `<tag>` or `<tag ...>` not preceded by `/`.
    const openMatches = out.match(new RegExp(`<${tag}(?:\\s[^>]*)?>`, 'gi')) || [];
    const closeMatches = out.match(new RegExp(`<\\/${tag}\\s*>`, 'gi')) || [];
    const missing = openMatches.length - closeMatches.length;
    if (missing > 0) {
      // eslint-disable-next-line no-console
      console.warn(`${source} index.html has ${missing} unclosed <${tag}> — auto-closing.`);
      out += '\n' + `</${tag}>`.repeat(missing);
    }
  }
  return out;
}

function buildFileTree(
  files: BundleOptions['files'],
  framework: string,
): FileSystemTree {
  const tree: FileSystemTree = {};

  // Collect all file paths first so we can detect conflicts
  const allPaths = new Set<string>();
  for (const f of files) {
    if (!f.content) continue;
    const fp = f.filePath && f.filePath !== '/'
      ? `${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`
      : f.fileName;
    allPaths.add(fp);
  }

  // Write all session files
  for (const f of files) {
    if (!f.content) continue;

    // Never include sensitive files in the preview
    if (/^\.env($|\.)/.test(f.fileName)) continue;
    // Skip non-source files that don't affect the preview
    if (/\.(sql|md)$/.test(f.fileName)) continue;
    if (f.filePath?.includes('supabase/')) continue;

    const fullPath = f.filePath && f.filePath !== '/'
      ? `${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`
      : f.fileName;

    // Skip files whose path treats an existing file as a directory
    // e.g., skip "src/App.tsx/App.tsx" if "src/App.tsx" exists as a file
    const segments = fullPath.split('/');
    let skipFile = false;
    for (let i = 1; i < segments.length; i++) {
      const parentPath = segments.slice(0, i).join('/');
      if (allPaths.has(parentPath) && parentPath !== fullPath) {
        skipFile = true;
        break;
      }
    }
    if (skipFile) continue;

    // Sanitize JS/TS files: deduplicate imports to prevent "already declared" errors
    let content = f.content;
    if (/\.(tsx?|jsx?|mjs)$/.test(f.fileName)) {
      content = stripConflictingStubs(content);
      content = deduplicateImports(content, fullPath);

      // Auto-fix missing default export: if a component file has a named export
      // but no default export, add one. Prevents "does not provide an export named 'default'" errors.
      if (content && !/export\s+default\b/.test(content)) {
        // Try named export first: export function/const/class ComponentName
        const namedExportMatch = content.match(/export\s+(?:function|const|class)\s+([A-Z]\w*)/);
        if (namedExportMatch) {
          content += `\nexport default ${namedExportMatch[1]};\n`;
        } else {
          // Try unexported function/const matching the filename
          const compName = f.fileName.replace(/\.(tsx?|jsx?)$/, '');
          const fnMatch = content.match(new RegExp(`(?:function|const|class)\\s+(${compName})\\b`));
          if (fnMatch) {
            content += `\nexport default ${fnMatch[1]};\n`;
          } else if (/^[A-Z]/.test(compName) && /return\s*\(/.test(content)) {
            // File looks like a component (PascalCase name + JSX return) but has no matching declaration
            // Wrap the whole thing as a default export won't work — just add a stub
            content += `\nexport default function ${compName}() { return null; }\n`;
          }
        }
      }
    }

    // Create nested directory structure
    const parts = fullPath.split('/');
    let current: any = tree;
    let pathConflict = false;
    for (let i = 0; i < parts.length - 1; i++) {
      if (current[parts[i]]?.file) {
        // A file already exists at this path segment (e.g., src/App.tsx is a file
        // but we're trying to create src/App.tsx/App.tsx as if App.tsx were a directory).
        // Skip this invalid file entirely.
        pathConflict = true;
        break;
      }
      if (!current[parts[i]]) {
        current[parts[i]] = { directory: {} };
      }
      current = current[parts[i]].directory;
    }
    if (pathConflict) continue;
    // Don't overwrite an existing directory node with a file
    const fileName = parts[parts.length - 1];
    if (current[fileName]?.directory && Object.keys(current[fileName].directory).length > 0) continue;
    current[fileName] = {
      file: { contents: content },
    };
  }

  // Ensure index.html exists — includes dark class for shadcn/Tailwind theme support
  // Tailwind v4 via CDN — loaded as <script> which Vite does NOT intercept.
  // PostCSS/WASI fails silently in WebContainer, so CDN is the primary Tailwind engine.
  // The CDN script scans the DOM for utility classes and generates CSS on the fly.
  // Config is injected via JS after the CDN loads to avoid Vite CSS interception.
  const tailwindCdnBlock = `<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
<script data-tasking-tw-config>
(function(){
  function injectConfig() {
    var s = document.createElement('style');
    s.setAttribute('type', 'text/tailwindcss');
    s.textContent = [
      '@import "tailwindcss";',
      '@custom-variant dark (&:is(.dark *));',
      '@theme inline {',
      '  --color-background: hsl(var(--background));',
      '  --color-foreground: hsl(var(--foreground));',
      '  --color-card: hsl(var(--card));',
      '  --color-card-foreground: hsl(var(--card-foreground));',
      '  --color-popover: hsl(var(--popup));',
      '  --color-popover-foreground: hsl(var(--popup-foreground));',
      '  --color-primary: hsl(var(--primary));',
      '  --color-primary-foreground: hsl(var(--primary-foreground));',
      '  --color-secondary: hsl(var(--secondary));',
      '  --color-secondary-foreground: hsl(var(--secondary-foreground));',
      '  --color-muted: hsl(var(--muted));',
      '  --color-muted-foreground: hsl(var(--muted-foreground));',
      '  --color-accent: hsl(var(--accent));',
      '  --color-accent-foreground: hsl(var(--accent-foreground));',
      '  --color-destructive: hsl(var(--destructive));',
      '  --color-destructive-foreground: hsl(var(--destructive-foreground));',
      '  --color-border: hsl(var(--border));',
      '  --color-input: hsl(var(--input));',
      '  --color-ring: hsl(var(--ring));',
      '  --color-surface: hsl(var(--surface));',
      '  --color-surface-foreground: hsl(var(--surface-foreground));',
      '  --color-sidebar: hsl(var(--sidebar));',
      '  --color-sidebar-foreground: hsl(var(--sidebar-foreground));',
      '  --font-sans: Inter, system-ui, -apple-system, sans-serif;',
      '  --font-mono: JetBrains Mono, Fira Code, monospace;',
      '}'
    ].join('\\n');
    document.head.appendChild(s);
  }
  // The CDN script processes <style type="text/tailwindcss"> blocks.
  // Inject config AFTER CDN loads (script tags are blocking, so this runs after).
  injectConfig();
})();
</script>`;

  // CSS vars + Tailwind CDN injected into index.html
  const themeStyleBlock = `${tailwindCdnBlock}
<style data-tasking-theme>
:root {
  --background: 220 15% 8%; --foreground: 220 10% 97%;
  --surface: 220 15% 11%; --surface-foreground: 220 10% 95%;
  --sidebar: 220 15% 10%; --sidebar-foreground: 220 10% 80%;
  --card: 220 15% 13%; --card-foreground: 220 10% 95%;
  --popup: 220 15% 15%; --popup-foreground: 220 10% 98%;
  --primary: 25 90% 55%; --primary-foreground: 25 10% 10%;
  --secondary: 220 10% 25%; --secondary-foreground: 220 10% 90%;
  --accent: 145 70% 45%; --accent-foreground: 145 10% 5%;
  --muted: 220 10% 18%; --muted-foreground: 220 10% 65%;
  --error: 0 80% 60%; --error-foreground: 0 10% 98%;
  --destructive: 0 80% 60%; --destructive-foreground: 0 10% 98%;
  --warning: 45 90% 50%; --warning-foreground: 45 10% 10%;
  --success: 145 70% 45%; --success-foreground: 145 10% 5%;
  --info: 200 80% 50%; --info-foreground: 200 10% 95%;
  --border: 220 10% 22%; --input: 220 10% 22%; --ring: 25 90% 55%;
  --radius: 12px;
  color-scheme: dark;
}
html, body { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
</style>`;

  if (!tree['index.html']) {
    const mountId = framework === 'vue' ? 'app' : framework === 'svelte' ? 'app' : 'root';
    tree['index.html'] = {
      file: {
        contents: `<!DOCTYPE html>
<html lang="en" class="dark" style="color-scheme: dark">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Preview</title>
  ${themeStyleBlock}
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300..900&family=Space+Grotesk:wght@300..700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet" />
</head>
<body>
  <div id="${mountId}"></div>
  <script type="module" src="/${detectEntry(files, framework)}"></script>
  ${PREVIEW_INSPECTOR_SCRIPT}
</body>
</html>`,
      },
    };
  } else {
    let htmlContent = (tree['index.html'] as any).file.contents as string;

    htmlContent = sanitizeRawTextElements(htmlContent, '[buildFileTree]');

    // If index.html exists but is missing dark class, inject it
    if (!htmlContent.includes('class="dark"') && !htmlContent.includes("class='dark'")) {
      htmlContent = htmlContent.replace(
        /<html([^>]*)>/,
        '<html$1 class="dark" style="color-scheme: dark">',
      );
    }

    // Inject theme CSS variables if not already present
    if (!htmlContent.includes('data-tasking-theme')) {
      htmlContent = htmlContent.replace('</head>', `  ${themeStyleBlock}\n</head>`);
    }

    // Inject visual editor inspector script if not already present
    if (!htmlContent.includes('data-tasking-preview-inspector')) {
      if (htmlContent.includes('</body>')) {
        htmlContent = htmlContent.replace('</body>', `  ${PREVIEW_INSPECTOR_SCRIPT}\n</body>`);
      } else {
        htmlContent += `\n${PREVIEW_INSPECTOR_SCRIPT}`;
      }
    }

    // If index.html has no module script pointing to a source entry, inject one.
    // Repos often have index.html referencing built files (dist/bundle.js) which don't exist.
    const hasModuleScript = /<script[^>]*type\s*=\s*["']module["'][^>]*src\s*=\s*["'][^"']*\.(tsx?|jsx?|mjs)["']/i.test(htmlContent) ||
      /<script[^>]*src\s*=\s*["'][^"']*\.(tsx?|jsx?|mjs)["'][^>]*type\s*=\s*["']module["']/i.test(htmlContent);
    if (!hasModuleScript) {
      const entry = detectEntry(files, framework);
      const scriptTag = `<script type="module" src="/${entry}"></script>`;
      if (htmlContent.includes('</body>')) {
        htmlContent = htmlContent.replace('</body>', `  ${scriptTag}\n</body>`);
      } else {
        htmlContent += `\n${scriptTag}`;
      }
    }

    tree['index.html'] = { file: { contents: htmlContent } };
  }

  // Always ensure package.json has required deps for Vite preview
  {
    const requiredDeps: Record<string, string> = {};
    const requiredDevDeps: Record<string, string> = { vite: '^6' };

    if (framework === 'react' || framework === 'unknown') {
      requiredDeps['react'] = '^19';
      requiredDeps['react-dom'] = '^19';
      requiredDevDeps['@vitejs/plugin-react'] = '^4';
    }
    if (framework === 'vue') {
      requiredDeps['vue'] = '^3';
      requiredDevDeps['@vitejs/plugin-vue'] = '^5';
    }
    if (framework === 'svelte') {
      requiredDeps['svelte'] = '^5';
      requiredDevDeps['@sveltejs/vite-plugin-svelte'] = '^5';
    }

    // Detect additional deps from imports in files
    const allContent = files.map((f) => f.content || '').join('\n');
    if (/from\s+['"]react-router/.test(allContent)) requiredDeps['react-router-dom'] = '^7';
    if (/from\s+['"]lucide-react/.test(allContent)) requiredDeps['lucide-react'] = '^0.500';
    if (/from\s+['"]framer-motion/.test(allContent)) requiredDeps['framer-motion'] = '^12';
    if (/from\s+['"]next-themes/.test(allContent)) requiredDeps['next-themes'] = '^0.4';
    if (/from\s+['"]sonner/.test(allContent)) requiredDeps['sonner'] = '^2';
    if (/from\s+['"]clsx/.test(allContent)) requiredDeps['clsx'] = '^2';
    if (/from\s+['"]tailwind-merge/.test(allContent)) requiredDeps['tailwind-merge'] = '^3';
    if (/from\s+['"]class-variance-authority/.test(allContent)) requiredDeps['class-variance-authority'] = '^0.7';
    // Tailwind v4 is loaded via CDN <script> in index.html — no npm deps needed.
    // PostCSS/WASI doesn't work in WebContainer's browser-based Node.js.

    // shadcn component dependencies — these are commonly used but not directly imported by user code
    if (/from\s+['"]date-fns/.test(allContent)) requiredDeps['date-fns'] = '^3';
    if (/from\s+['"]react-day-picker/.test(allContent)) requiredDeps['react-day-picker'] = '^8';
    if (/from\s+['"]recharts/.test(allContent)) requiredDeps['recharts'] = '^2';
    if (/from\s+['"]cmdk/.test(allContent)) requiredDeps['cmdk'] = '^1';
    if (/from\s+['"]embla-carousel-react/.test(allContent)) requiredDeps['embla-carousel-react'] = '^8';
    if (/from\s+['"]react-resizable-panels/.test(allContent)) requiredDeps['react-resizable-panels'] = '^2';
    if (/from\s+['"]vaul/.test(allContent)) requiredDeps['vaul'] = '^1';
    if (/from\s+['"]input-otp/.test(allContent)) requiredDeps['input-otp'] = '^1';
    if (/from\s+['"]react-hook-form/.test(allContent)) requiredDeps['react-hook-form'] = '^7';
    if (/from\s+['"]@hookform\/resolvers/.test(allContent)) requiredDeps['@hookform/resolvers'] = '^3';
    if (/from\s+['"]zod/.test(allContent)) requiredDeps['zod'] = '^3';
    if (/from\s+['"]tailwindcss-animate/.test(allContent)) requiredDeps['tailwindcss-animate'] = '^1';
    if (/from\s+['"]@tanstack\/react-query/.test(allContent)) requiredDeps['@tanstack/react-query'] = '^5';
    if (/from\s+['"]@supabase\/supabase-js/.test(allContent)) requiredDeps['@supabase/supabase-js'] = '^2';
    if (/from\s+['"]pdf-lib/.test(allContent)) { requiredDeps['pdf-lib'] = '^1'; requiredDeps['@pdf-lib/fontkit'] = '^1'; }
    if (/from\s+['"]qrcode\.react/.test(allContent)) requiredDeps['qrcode.react'] = '^4';
    if (/from\s+['"]perfect-freehand/.test(allContent)) requiredDeps['perfect-freehand'] = '^1';

    // Auto-detect Svelte deps from .svelte files or svelte imports
    if (framework === 'svelte' || /\.svelte/.test(files.map(f => f.fileName).join(' '))) {
      if (!requiredDeps['svelte']) requiredDeps['svelte'] = '^5';
      if (!requiredDevDeps['@sveltejs/vite-plugin-svelte']) requiredDevDeps['@sveltejs/vite-plugin-svelte'] = '^5';
    }

    // Auto-detect Radix UI primitives from imports
    const radixPackages = allContent.matchAll(/from\s+['"](@radix-ui\/react-[a-z-]+)/g);
    for (const match of radixPackages) {
      requiredDeps[match[1]] = '*';
    }

    // Auto-detect any @/ imports that use shadcn-style components — ensure clsx + twMerge
    if (/@\/components\/ui|@\/lib\/utils/.test(allContent)) {
      if (!requiredDeps['clsx']) requiredDeps['clsx'] = '^2';
      if (!requiredDeps['tailwind-merge']) requiredDeps['tailwind-merge'] = '^3';
    }

    // ── Generic import scanner ──
    // Catches ALL npm packages imported in session files (zustand, three, @react-three/fiber, etc.)
    // so we never miss a dependency. Packages already in requiredDeps keep their pinned version;
    // newly discovered ones get '*' (latest).
    {
      const nodeBuiltins = new Set(['fs', 'path', 'os', 'crypto', 'http', 'https', 'url', 'util',
        'stream', 'events', 'buffer', 'child_process', 'net', 'tls', 'dns', 'dgram', 'cluster',
        'readline', 'zlib', 'querystring', 'string_decoder', 'assert', 'timers', 'tty', 'v8',
        'vm', 'worker_threads', 'perf_hooks', 'async_hooks', 'inspector', 'module', 'process',
        'console', 'constants']);

      // Build set of project-internal directory/file names so we don't treat them as npm packages.
      // e.g. if session has "components/Button.tsx", "utils/helpers.ts", "store/gameStore.ts",
      // then "components", "utils", "store" are NOT npm packages.
      const projectPaths = new Set<string>();
      for (const f of files) {
        const fp = f.filePath && f.filePath !== '/'
          ? `${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`
          : f.fileName;
        // Add each top-level directory: "src/components/Button.tsx" → "src"
        const topDir = fp.split('/')[0];
        if (topDir) projectPaths.add(topDir.replace(/\.(tsx?|jsx?|mjs|cjs|css|scss|json|html)$/, ''));
        // Also add second-level dirs for "src/X" pattern: "src/components/Button.tsx" → "components"
        const parts = fp.split('/');
        if (parts.length > 2 && parts[0] === 'src') {
          projectPaths.add(parts[1]);
        }
      }
      // Common project directory names that are never npm packages
      const projectDirNames = new Set([
        'components', 'pages', 'views', 'layouts', 'hooks', 'utils', 'helpers',
        'lib', 'services', 'api', 'store', 'stores', 'context', 'contexts',
        'types', 'models', 'interfaces', 'config', 'configs', 'constants',
        'assets', 'images', 'icons', 'fonts', 'styles', 'css', 'theme',
        'features', 'modules', 'routes', 'router', 'navigation',
        'middleware', 'plugins', 'providers', 'wrappers', 'hoc',
        'data', 'fixtures', 'mocks', 'tests', '__tests__', 'spec',
        'app', 'core', 'shared', 'common', 'base', 'ui',
        'sections', 'containers', 'screens', 'partials', 'blocks',
        'state', 'reducers', 'actions', 'selectors', 'slices',
        'graphql', 'queries', 'mutations', 'subscriptions',
        'env', 'public', 'static', 'dist', 'build', 'output',
      ]);

      // Match bare specifiers: "zustand", "@react-three/fiber", etc.
      // Excludes relative paths (./), absolute paths (/), and @/ path aliases (shadcn)
      const importRe = /(?:from\s+['"]|require\s*\(\s*['"])([^./'"@\s][^'"]*|@[a-z0-9][^/'"]*\/[^'"]+)/g;
      for (const match of allContent.matchAll(importRe)) {
        const raw = match[1];
        // Resolve to package name (strip sub-paths): e.g. "zustand/middleware" → "zustand",
        // "@react-three/fiber/native" → "@react-three/fiber"
        let pkgName: string;
        if (raw.startsWith('@')) {
          // Skip @/ path aliases (e.g. @/components/ui/button)
          if (raw.startsWith('@/')) continue;
          const parts = raw.split('/');
          pkgName = parts.slice(0, 2).join('/');
        } else {
          pkgName = raw.split('/')[0];
        }
        if (!pkgName || pkgName.startsWith('.') || pkgName.startsWith('/')) continue;
        if (nodeBuiltins.has(pkgName) || pkgName.startsWith('node:')) continue;
        // Skip project-internal imports (e.g. "components/Button", "utils/helpers")
        if (projectPaths.has(pkgName) || projectDirNames.has(pkgName)) continue;
        if (!requiredDeps[pkgName] && !requiredDevDeps[pkgName]) {
          requiredDeps[pkgName] = '*';
        }
      }
    }

    // Merge into existing package.json or create new one
    let pkg: any = { name: 'preview-app', private: true, type: 'module', scripts: { dev: 'vite --host 0.0.0.0' }, dependencies: {}, devDependencies: {} };
    if (tree['package.json']) {
      try {
        pkg = JSON.parse((tree['package.json'] as any).file.contents);
        if (!pkg.dependencies) pkg.dependencies = {};
        if (!pkg.devDependencies) pkg.devDependencies = {};
      } catch { /* use default */ }
    }

    // Trim unused dependencies — only keep packages actually imported in session files.
    // This prevents WebContainer from downloading 30+ unused packages (60+ second installs).
    {
      const importedPackages = new Set<string>();
      // Match: import ... from 'pkg' / from "pkg" / require('pkg')
      const importRe = /(?:from\s+['"]|require\s*\(\s*['"])(@?[a-z0-9][\w.\-]*(?:\/[a-z0-9][\w.\-]*)?)/gi;
      for (const match of allContent.matchAll(importRe)) {
        importedPackages.add(match[1]);
        // Also add the scope root for scoped packages: @radix-ui/react-dialog → @radix-ui/react-dialog
        const scopeMatch = match[1].match(/^(@[^/]+\/[^/]+)/);
        if (scopeMatch) importedPackages.add(scopeMatch[1]);
      }
      // Always keep framework-essential packages
      const essentialPkgs = new Set(['react', 'react-dom', 'vue', 'svelte', 'vite', '@vitejs/plugin-react',
        '@vitejs/plugin-vue', '@sveltejs/vite-plugin-svelte',
        'tailwind-merge', 'clsx', 'class-variance-authority']);

      for (const section of ['dependencies', 'devDependencies'] as const) {
        const deps = pkg[section];
        for (const depName of Object.keys(deps)) {
          if (essentialPkgs.has(depName)) continue;
          if (importedPackages.has(depName)) continue;
          // Keep if any file imports a sub-path: @radix-ui/react-dialog/something
          const isUsed = [...importedPackages].some(p => p.startsWith(depName + '/') || p === depName);
          if (!isUsed) {
            delete deps[depName];
          }
        }
      }
    }

    // Fix React Three Fiber + React 19 incompatibility:
    // @react-three/fiber v8 uses react-reconciler internals that were renamed in React 19.
    // Pin React to v18 when R3F is present to avoid "Cannot read properties of undefined (reading 'ReactCurrentOwner')"
    {
      const allDepsCheck = { ...pkg.dependencies, ...pkg.devDependencies, ...requiredDeps };
      if (allDepsCheck['@react-three/fiber'] || allDepsCheck['@react-three/drei']) {
        pkg.dependencies['react'] = '^18.3.1';
        pkg.dependencies['react-dom'] = '^18.3.1';
        // Also pin @types/react if present
        if (pkg.devDependencies['@types/react']) pkg.devDependencies['@types/react'] = '^18';
        if (pkg.devDependencies['@types/react-dom']) pkg.devDependencies['@types/react-dom'] = '^18';
      }
    }

    // Keep Tailwind v4 installed — PostCSS handles @import "tailwindcss" natively.
    // Remove only @tailwindcss/vite (use @tailwindcss/postcss instead in WebContainer).
    // Use caret ranges so security/patch updates flow through; lockfile (when
    // present) supplies byte-identical resolution at install time.
    // Floor at 4.1 specifically: <4.1 had v3-shape postcss plugin export mismatches.
    delete pkg.dependencies['@tailwindcss/vite'];
    delete pkg.devDependencies['@tailwindcss/vite'];
    if (!pkg.devDependencies['tailwindcss'] && !pkg.dependencies['tailwindcss']) {
      pkg.devDependencies['tailwindcss'] = '^4.1.18';
    }
    if (!pkg.devDependencies['@tailwindcss/postcss'] && !pkg.dependencies['@tailwindcss/postcss']) {
      pkg.devDependencies['@tailwindcss/postcss'] = '^4.1.18';
    }
    if (!pkg.devDependencies['postcss'] && !pkg.dependencies['postcss']) {
      pkg.devDependencies['postcss'] = '^8.5.6';
    }

    // Ensure required deps exist (don't overwrite user-specified versions)
    for (const [k, v] of Object.entries(requiredDeps)) {
      if (!pkg.dependencies[k]) pkg.dependencies[k] = v;
    }
    for (const [k, v] of Object.entries(requiredDevDeps)) {
      if (!pkg.devDependencies[k] && !pkg.dependencies[k]) pkg.devDependencies[k] = v;
    }
    // Ensure scripts.dev exists
    if (!pkg.scripts) pkg.scripts = {};
    if (!pkg.scripts.dev) pkg.scripts.dev = 'vite --host 0.0.0.0';
    if (!pkg.type) pkg.type = 'module';

    // Log final dependency list for debugging install failures
    const allDeps = { ...pkg.dependencies, ...pkg.devDependencies };
    const depNames = Object.keys(allDeps).sort();
    console.debug('[buildFileTree] Final deps (' + depNames.length + '):', depNames.join(', '));

    tree['package.json'] = { file: { contents: JSON.stringify(pkg, null, 2) } };
  }

  // Force v4-compatible postcss config. We ALWAYS install Tailwind v4 +
  // @tailwindcss/postcss above, so any model-generated v3 config (e.g.
  // `{ plugins: { tailwindcss: {} } }`) would resolve `tailwindcss` as a
  // postcss plugin and fail with ENOENT — the v4 package no longer exposes
  // itself as a postcss plugin. Overwrite any existing config to guarantee
  // the canonical v4 + autoprefixer setup.
  delete tree['postcss.config.cjs'];
  delete tree['postcss.config.mjs'];
  delete tree['.postcssrc'];
  delete tree['.postcssrc.js'];
  delete tree['.postcssrc.json'];
  tree['postcss.config.js'] = {
    file: {
      contents: `export default {\n  plugins: {\n    '@tailwindcss/postcss': {},\n  },\n};\n`,
    },
  };

  // CSS files are left as-is — Tailwind v4 + @tailwindcss/postcss handles everything natively
  // Ensure src/index.css exists with theme variables if missing
  {
    if (!tree['src']) tree['src'] = { directory: {} };
    const srcDir = (tree['src'] as any).directory;
    if (!srcDir['index.css']) {
      srcDir['index.css'] = { file: { contents: `@import "tailwindcss";\n\n${SHADCN_TAILWIND_DIRECTIVES}` } };
    }

      // Fix @apply border-border in ALL CSS files (breaks in Tailwind v4)
      // Replace with plain CSS equivalents
      const fixApplyInTree = (node: any) => {
        for (const key of Object.keys(node)) {
          if (node[key]?.directory) {
            fixApplyInTree(node[key].directory);
          } else if (node[key]?.file?.contents && /\.css$/.test(key)) {
            let css = node[key].file.contents as string;
            if (/@apply\s+border-border/.test(css) || /@apply\s+bg-background/.test(css)) {
              css = css
                .replace(/@apply\s+border-border\s*;?/g, 'border-color: var(--color-border, var(--border));')
                .replace(/@apply\s+bg-background\s+text-foreground\s*;?/g, 'background-color: var(--color-background, var(--background)); color: var(--color-foreground, var(--foreground));')
                .replace(/@apply\s+bg-background\s*;?/g, 'background-color: var(--color-background, var(--background));')
                .replace(/@apply\s+text-foreground\s*;?/g, 'color: var(--color-foreground, var(--foreground));');
              node[key] = { file: { contents: css } };
            }
          }
        }
      };
      fixApplyInTree(tree);

      // Ensure the entry point imports the CSS file
      const entryFile = detectEntry(files, framework);
      const entryParts = entryFile.split('/');
      let entryNode: any = tree;
      for (let i = 0; i < entryParts.length - 1; i++) {
        if (entryNode[entryParts[i]]?.directory) {
          entryNode = entryNode[entryParts[i]].directory;
        } else {
          entryNode = null;
          break;
        }
      }
      if (entryNode) {
        const entryFileName = entryParts[entryParts.length - 1];
        const entryContent = entryNode[entryFileName]?.file?.contents || '';
        if (entryContent && !entryContent.includes('index.css') && !entryContent.includes('globals.css')) {
          // Prepend CSS import to the entry file
          entryNode[entryFileName] = {
            file: { contents: `import './index.css';\n${entryContent}` },
          };
        }
      }
  }

  // Ensure tsconfig.json always exists with correct settings for the preview bundler
  {
    const SAFE_TSCONFIG = {
      compilerOptions: {
        allowImportingTsExtensions: true,
        isolatedModules: true,
        jsx: 'react-jsx',
        lib: ['ES2020', 'DOM', 'DOM.Iterable'],
        module: 'ESNext',
        moduleDetection: 'force',
        moduleResolution: 'bundler',
        noEmit: true,
        noFallthroughCasesInSwitch: false,
        noImplicitAny: false,
        noUnusedLocals: false,
        noUnusedParameters: false,
        paths: { '@/*': ['./src/*'], '$lib/*': ['./src/lib/*'] },
        skipLibCheck: true,
        strict: false,
        target: 'ES2020',
        useDefineForClassFields: true,
      },
      include: ['src'],
    };

    if (tree['tsconfig.json']) {
      const tscContent = (tree['tsconfig.json'] as any).file?.contents || '';
      // Fix broken extends (e.g. .svelte-kit/tsconfig.json, .next/, .nuxt/)
      if (tscContent.includes('.svelte-kit') || tscContent.includes('.next/') || tscContent.includes('.nuxt/')) {
        tree['tsconfig.json'] = { file: { contents: JSON.stringify(SAFE_TSCONFIG, null, 2) } };
      } else {
        // Merge our required settings into the existing tsconfig
        try {
          const parsed = JSON.parse(tscContent);
          delete parsed.extends;
          parsed.compilerOptions = { ...SAFE_TSCONFIG.compilerOptions, ...(parsed.compilerOptions || {}) };
          // Always enforce these critical settings
          parsed.compilerOptions.jsx = 'react-jsx';
          parsed.compilerOptions.skipLibCheck = true;
          parsed.compilerOptions.noImplicitAny = false;
          parsed.compilerOptions.strict = false;
          parsed.compilerOptions.noUnusedLocals = false;
          parsed.compilerOptions.noUnusedParameters = false;
          parsed.compilerOptions.paths = { '@/*': ['./src/*'], '$lib/*': ['./src/lib/*'], ...(parsed.compilerOptions.paths || {}) };
          if (!parsed.include) parsed.include = ['src'];
          tree['tsconfig.json'] = { file: { contents: JSON.stringify(parsed, null, 2) } };
        } catch {
          tree['tsconfig.json'] = { file: { contents: JSON.stringify(SAFE_TSCONFIG, null, 2) } };
        }
      }
    } else {
      // No tsconfig at all — inject one
      tree['tsconfig.json'] = { file: { contents: JSON.stringify(SAFE_TSCONFIG, null, 2) } };
    }
  }

  // Always generate vite.config with resolve.extensions for extensionless imports
  // (e.g. "./Movies" → "./Movies.jsx"). Overwrite any user-provided config to ensure
  // extensions are always set — user plugins are auto-detected from file content.
  // Remove any existing user config to avoid conflicts
  delete tree['vite.config.js'];
  delete tree['vite.config.ts'];
  delete tree['vite.config.mjs'];
  delete tree['vite.config.mts'];

  // Keep postcss.config — Tailwind v4 uses @tailwindcss/postcss

  const allContent = files.map((f) => f.content || '').join('\n');
  let configContent: string;
  // Detect if files use @/ path alias
  const usesAtAlias = /@\//.test(allContent);

  // Vite plugin that proxies external images to bypass COEP restrictions.
  // Intercepts /_api/imgproxy?url=<encoded-url> and fetches the image server-side.
  const imgProxyPlugin = `{
    name: 'imgproxy',
    configureServer(server) {
      server.middlewares.use((req, res, next) => {
        if (!req.url || !req.url.includes('/_api/imgproxy')) return next();
        const qs = new URL(req.url, 'http://localhost').searchParams;
        let target = qs.get('url');
        if (!target) { res.statusCode = 400; res.end('Missing url'); return; }
        // Unwrap double-proxied URLs: /_api/imgproxy?url=/_api/imgproxy?url=https://...
        // Keep unwrapping until we get a real https:// URL
        for (let i = 0; i < 5; i++) {
          try { target = decodeURIComponent(target); } catch {}
          if (/^\\/_api\\/imgproxy/.test(target)) {
            const inner = new URL(target, 'http://localhost').searchParams.get('url');
            if (inner) { target = inner; continue; }
          }
          break;
        }
        // Final decode pass
        try { if (/%[0-9A-Fa-f]{2}/.test(target)) target = decodeURIComponent(target); } catch {}
        // Must be an absolute URL
        if (!/^https?:\\/\\//i.test(target)) { res.statusCode = 400; res.end('Invalid URL: ' + target.substring(0, 100)); return; }
        fetch(target, { redirect: 'follow' })
          .then(async (r) => {
            if (!r.ok) { res.statusCode = r.status; res.end('Upstream error'); return; }
            res.setHeader('Content-Type', r.headers.get('content-type') || 'application/octet-stream');
            res.setHeader('Cache-Control', 'public, max-age=86400');
            res.setHeader('Access-Control-Allow-Origin', '*');
            const buf = new Uint8Array(await r.arrayBuffer());
            res.end(buf);
          })
          .catch((err) => { res.statusCode = 502; res.end('Failed: ' + (err.message || '').substring(0, 100)); });
      });
    }
  }`;

  // Vite transform plugin that rewrites external image URLs to go through the
  // imgproxy middleware, bypassing COEP restrictions in WebContainer iframes.
  const imgRewritePlugin = `{
    name: 'img-rewrite',
    enforce: 'pre',
    transform(code, id) {
      if (/node_modules/.test(id)) return null;
      if (!/\\.(jsx|tsx|js|ts|svelte|vue|html|css)$/.test(id)) return null;
      const IMG_HOSTS = [
        'picsum.photos',
        'images.unsplash.com',
        'source.unsplash.com',
        'via.placeholder.com',
        'placekitten.com',
        'placehold.co',
        'placehold.it',
        'placeimg.com',
        'loremflickr.com',
        'dummyimage.com',
        'fakeimg.pl',
        'cloudflare-ipfs.com',
        'avatars.githubusercontent.com',
        'i.imgur.com',
        'lh3.googleusercontent.com',
        'robohash.org',
        'ui-avatars.com',
        'api.dicebear.com',
        'cdn.pixabay.com',
        'media.giphy.com',
        'fakestoreapi.com',
      ];
      const IMG_EXTS = /\\.(png|jpe?g|gif|webp|svg|avif|ico|bmp|tiff?)([?#]|$)/i;
      let changed = false;
      // Skip files that are already fully proxied — avoid double-wrapping
      const result = code.replace(/(["'\`])(https:\\/\\/[^"'\`\\s]{8,}?)\\1/g, (match, q, url) => {
        try {
          // Skip if this URL is already inside a /_api/imgproxy wrapper
          const matchIdx = code.indexOf(match);
          if (matchIdx > 0) {
            const before = code.substring(Math.max(0, matchIdx - 30), matchIdx);
            if (/imgproxy\\?url=/.test(before)) return match;
          }
          const host = new URL(url).hostname;
          const isImgHost = IMG_HOSTS.some(h => host === h || host.endsWith('.' + h));
          if (!isImgHost && !IMG_EXTS.test(url)) return match;
          changed = true;
          return q + '/_api/imgproxy?url=' + encodeURIComponent(url) + q;
        } catch { return match; }
      });
      return changed ? { code: result, map: null } : null;
    },
    transformIndexHtml(html) {
      return html.replace(/(src|href|poster|content)=(["'])(https:\\/\\/[^"']+?)\\2/gi, (match, attr, q, url) => {
        try {
          const host = new URL(url).hostname;
          const IMG_HOSTS = ['picsum.photos','images.unsplash.com','source.unsplash.com','via.placeholder.com','placekitten.com','placehold.co','placehold.it','placeimg.com','loremflickr.com','dummyimage.com','fakeimg.pl','avatars.githubusercontent.com','i.imgur.com','robohash.org','ui-avatars.com','api.dicebear.com','cdn.pixabay.com','media.giphy.com','fakestoreapi.com'];
          const isImgHost = IMG_HOSTS.some(h => host === h || host.endsWith('.' + h));
          if (!isImgHost && !/\\.(png|jpe?g|gif|webp|svg|avif|ico)([?#]|$)/i.test(url)) return match;
          return attr + '=' + q + '/_api/imgproxy?url=' + encodeURIComponent(url) + q;
        } catch { return match; }
      });
    }
  }`;

  if (framework === 'vue') {
    configContent = `import { defineConfig } from 'vite'
import vue from '@vitejs/plugin-vue'
import path from 'path'
export default defineConfig({
  plugins: [vue(), ${imgProxyPlugin}, ${imgRewritePlugin}],
  resolve: {
    extensions: ['.mjs', '.js', '.mts', '.ts', '.jsx', '.tsx', '.json', '.vue'],
    alias: { '@': path.resolve(__dirname, 'src') }
  }
})`;
  } else if (framework === 'svelte') {
    const hasTailwind = /@tailwindcss|tailwindcss|bg-background|text-foreground/.test(allContent);
    const imports = [`import { svelte } from '@sveltejs/vite-plugin-svelte'`];
    const plugins = [`svelte()`, imgProxyPlugin, imgRewritePlugin];
    // Tailwind v4 uses @tailwindcss/postcss (not vite plugin) in WebContainer
    configContent = `import { defineConfig } from 'vite'
import path from 'path'
${imports.join('\n')}
export default defineConfig({
  plugins: [${plugins.join(', ')}],
  resolve: {
    extensions: ['.mjs', '.js', '.mts', '.ts', '.jsx', '.tsx', '.json', '.svelte'],
    alias: { '@': path.resolve(__dirname, 'src'), '$lib': path.resolve(__dirname, 'src/lib') }
  }
})`;
  } else if (framework === 'vanilla') {
    // Vanilla JS/TS (Three.js, PlayCanvas, etc.) — no framework plugin needed
    const hasTailwind = /@tailwindcss|tailwindcss|bg-background|text-foreground/.test(allContent);
    const plugins = [imgProxyPlugin, imgRewritePlugin];
    const imports: string[] = [];
    // Add SharedArrayBuffer headers + Three.js optimizations for game engine projects
    const needsSAB = /three|cannon|ammo|rapier|playcanvas|babylonjs|oimo/.test(allContent);
    const needsThree = /three/.test(allContent);
    const serverConfig = needsSAB ? `,
  server: {
    headers: {
      'Cross-Origin-Opener-Policy': 'same-origin',
      'Cross-Origin-Embedder-Policy': 'require-corp'
    }
  }` : '';
    const threeConfig = needsThree ? `,
  assetsInclude: ['**/*.gltf', '**/*.glb', '**/*.hdr', '**/*.exr', '**/*.bin'],
  optimizeDeps: {
    include: ['three'],
    esbuildOptions: { target: 'esnext' }
  },
  build: {
    target: 'esnext',
    rollupOptions: {
      output: {
        manualChunks: { three: ['three'] }
      }
    }
  }` : '';
    configContent = `import { defineConfig } from 'vite'
import path from 'path'
${imports.join('\n')}
export default defineConfig({
  plugins: [${plugins.join(', ')}],
  resolve: {
    extensions: ['.mjs', '.js', '.mts', '.ts', '.jsx', '.tsx', '.json'],
    alias: { '@': path.resolve(__dirname, 'src') }
  }${serverConfig}${threeConfig}
})`;
  } else {
    // React (default)
    const hasTailwind = /@tailwindcss|tailwindcss|@tailwind\s+base|bg-background|text-foreground|bg-card|bg-muted|@\/components\/ui|className.*bg-|className.*text-|className.*flex|className.*p-/.test(allContent);
    const plugins = [`react()`, imgProxyPlugin, imgRewritePlugin];
    const imports = [`import react from '@vitejs/plugin-react'`];
    // Tailwind v4 uses @tailwindcss/postcss (not vite plugin) in WebContainer
    configContent = `import { defineConfig } from 'vite'
import path from 'path'
${imports.join('\n')}
export default defineConfig({
  plugins: [${plugins.join(', ')}],
  resolve: {
    extensions: ['.mjs', '.js', '.mts', '.ts', '.jsx', '.tsx', '.json'],
    alias: { '@': path.resolve(__dirname, 'src') }
  }
})`;
  }
  // Use .mts extension to force ESM loading — prevents "ESM file cannot be loaded by require"
  // error when esbuild's externalize-deps plugin tries to require() ESM-only packages like @tailwindcss/vite
  tree['vite.config.mts'] = { file: { contents: configContent } };

  // ── Auto-fix: ensure Svelte entry point and svelte.config.js exist ──
  if (framework === 'svelte') {
    // Ensure svelte.config.js exists (needed by vite-plugin-svelte)
    if (!tree['svelte.config.js']) {
      tree['svelte.config.js'] = {
        file: { contents: `import { vitePreprocess } from '@sveltejs/vite-plugin-svelte';\nexport default { preprocess: vitePreprocess() };\n` },
      };
    }

    // Ensure src/main.ts entry exists
    if (!tree['src']) tree['src'] = { directory: {} };
    const srcDir = (tree['src'] as any).directory;
    if (!srcDir['main.ts'] && !srcDir['main.js']) {
      // Find App.svelte location
      const hasAppSvelte = srcDir['App.svelte'];
      const appImport = hasAppSvelte ? './App.svelte' : './App.svelte';
      srcDir['main.ts'] = {
        file: {
          contents: `import App from '${appImport}';\n\nconst app = new App({ target: document.getElementById('app')! });\n\nexport default app;\n`,
        },
      };
    }

    // Ensure src/App.svelte exists as a fallback
    if (!srcDir['App.svelte']) {
      // Check if there's an App.svelte at root level
      if (tree['App.svelte']) {
        // Move it to src/
        srcDir['App.svelte'] = tree['App.svelte'];
        delete tree['App.svelte'];
      }
    }

    // Remove SvelteKit-specific files/dirs that don't apply to plain Svelte
    // (tsconfig.json fix is handled universally above for all frameworks)
    delete tree['.svelte-kit'];
    // src/app.html is SvelteKit — plain Svelte uses index.html at root
    if ((tree['src'] as any).directory?.['app.html']) {
      delete (tree['src'] as any).directory['app.html'];
    }
    // Remove SvelteKit route files (+page.svelte, +layout.svelte, etc.)
    const removeSvelteKitRoutes = (node: any) => {
      for (const key of Object.keys(node)) {
        if (key.startsWith('+') && key.endsWith('.svelte')) {
          // Move +page.svelte content to App.svelte if App.svelte doesn't exist
          if (key === '+page.svelte' && !srcDir['App.svelte']) {
            srcDir['App.svelte'] = node[key];
          }
          delete node[key];
        } else if (node[key]?.directory) {
          removeSvelteKitRoutes(node[key].directory);
        }
      }
    };
    if ((tree['src'] as any).directory?.['routes']) {
      // Extract +page.svelte from routes/ as App.svelte
      const routesDir = (tree['src'] as any).directory['routes'];
      if (routesDir?.directory?.['+page.svelte'] && !srcDir['App.svelte']) {
        srcDir['App.svelte'] = routesDir.directory['+page.svelte'];
      }
      removeSvelteKitRoutes(routesDir.directory || {});
    }
  }

  // ── Auto-fix: ensure BrowserRouter exists somewhere when react-router-dom is used ──
  // Check App.tsx first (preferred location), then fall back to main.tsx entry point.
  {
    const allSrc = files.map((f) => f.content || '').join('\n');
    const usesReactRouter = /from\s+['"]react-router/.test(allSrc);

    if (usesReactRouter) {
      // Check if ANY file already has a Router wrapper
      const hasRouter = /BrowserRouter|RouterProvider|HashRouter|MemoryRouter/.test(allSrc);

      if (!hasRouter) {
        // Try to inject BrowserRouter into App.tsx first
        const appNode = tree['src'] && (tree['src'] as any).directory?.['App.tsx'];
        const appContent: string = appNode?.file?.contents || '';

        if (appContent && /Routes|useLocation|useNavigate/.test(appContent)) {
          // App.tsx uses router features — wrap its return JSX with BrowserRouter
          let fixed = appContent;
          if (!fixed.includes('BrowserRouter')) {
            // Add BrowserRouter to existing react-router-dom import or add new import
            if (/import\s*\{([^}]+)\}\s*from\s*['"]react-router-dom['"]/.test(fixed)) {
              fixed = fixed.replace(
                /import\s*\{([^}]+)\}\s*from\s*['"]react-router-dom['"]/,
                (m, imports) => `import { BrowserRouter, ${imports.trim()} } from 'react-router-dom'`,
              );
            } else {
              fixed = `import { BrowserRouter } from 'react-router-dom';\n${fixed}`;
            }
          }
          // Wrap the return content: find <Routes and wrap from there
          // Simple approach: wrap the outermost JSX return with BrowserRouter
          fixed = fixed.replace(
            /return\s*\(\s*\n?(\s*)</,
            'return (\n$1<BrowserRouter>\n$1',
          );
          // Add closing tag before the last );
          fixed = fixed.replace(
            /(\s*)\);\s*\}\s*$/,
            '$1</BrowserRouter>\n$1);\n}',
          );
          (tree['src'] as any).directory['App.tsx'] = { file: { contents: fixed } };
        } else {
          // Fallback: inject into main.tsx entry point
          const entryFile = detectEntry(files, framework);
          const entryParts = entryFile.split('/');
          let entryNode: any = tree;
          for (let i = 0; i < entryParts.length - 1; i++) {
            if (entryNode[entryParts[i]]?.directory) {
              entryNode = entryNode[entryParts[i]].directory;
            } else {
              entryNode = null;
              break;
            }
          }
          if (entryNode) {
            const entryFileName = entryParts[entryParts.length - 1];
            const entryContent: string = entryNode[entryFileName]?.file?.contents || '';
            if (entryContent) {
              let fixed = entryContent;
              fixed = `import { BrowserRouter } from 'react-router-dom';\n${fixed}`;
              fixed = fixed.replace(
                /(<App\s*\/?>)/,
                '<BrowserRouter>$1</BrowserRouter>',
              );
              entryNode[entryFileName] = { file: { contents: fixed } };
            }
          }
        }
      }
    }
  }

  return tree;
}

/** Detect the entry point file. */
function detectEntry(files: BundleOptions['files'], framework: string): string {
  const names = files.map((f) => f.fileName);
  const allNames = files.map((f) => {
    if (f.filePath && f.filePath !== '/') {
      return `${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`;
    }
    return f.fileName;
  });

  // Svelte entry points
  if (framework === 'svelte') {
    for (const candidate of [
      'src/main.ts', 'src/main.js',
      'main.ts', 'main.js',
    ]) {
      if (allNames.includes(candidate)) return candidate;
      const base = candidate.split('/').pop()!;
      const idx = names.indexOf(base);
      if (idx !== -1) return allNames[idx];
    }
    // Fallback: generate a main.ts that mounts App.svelte
    return 'src/main.ts';
  }

  // Check common entry points
  for (const candidate of [
    'src/main.tsx', 'src/main.ts', 'src/main.jsx', 'src/main.js',
    'src/index.tsx', 'src/index.ts', 'src/index.jsx', 'src/index.js',
    'main.tsx', 'main.ts', 'main.jsx', 'main.js',
    'index.tsx', 'index.ts', 'index.jsx', 'index.js',
    'src/App.tsx', 'src/App.jsx', 'App.tsx', 'App.jsx',
  ]) {
    // Check full path first (most accurate), then fallback to basename
    if (allNames.includes(candidate)) {
      return candidate;
    }
  }
  // Basename fallback: find the first matching basename and return its full path
  for (const candidate of [
    'main.tsx', 'main.ts', 'main.jsx', 'main.js',
    'index.tsx', 'index.ts', 'index.jsx', 'index.js',
    'App.tsx', 'App.jsx',
  ]) {
    const idx = names.indexOf(candidate);
    if (idx !== -1) return allNames[idx];
  }
  // Fallback: first TSX/JSX file (use full path)
  const jsxIdx = files.findIndex((f) => /\.(tsx|jsx)$/.test(f.fileName));
  return jsxIdx !== -1 ? allNames[jsxIdx] : 'src/main.tsx';
}

export interface WebContainerPreviewResult {
  url: string;
  errors: string[];
}

// Module-level error callback for Vite transform errors that occur after server-ready
let _onViteError: ((error: string) => void) | null = null;

/** Register a callback to receive Vite transform/compile errors after server start. */
export function onWebContainerError(cb: ((error: string) => void) | null): void {
  _onViteError = cb;
}

/**
 * Spawn a command inside the WebContainer (e.g. npm install <pkg>).
 * Returns the exit code. Streams stdout/stderr to the onOutput callback.
 */
export async function spawnInWebContainer(
  command: string,
  args: string[],
  onOutput?: (line: string) => void,
): Promise<number> {
  if (!_container) throw new Error('WebContainer not booted yet');
  const process = await _container.spawn(command, args);
  process.output.pipeTo(
    new WritableStream({
      write(chunk) {
        if (onOutput) onOutput(chunk);
      },
    }),
  );
  return process.exit;
}

/**
 * Start or update a Vite dev server in the WebContainer.
 * Returns the preview URL to use as iframe src.
 * Serialized: concurrent calls wait for the previous one to finish.
 */
export async function startWebContainerPreview(
  options: BundleOptions,
  sessionId: string,
  onLog?: (line: string) => void,
): Promise<WebContainerPreviewResult> {
  // Serialize: if a start is already in progress, wait for it then run ours
  if (_startingPreview) {
    try { await _startingPreview; } catch { /* ignore previous error */ }
  }

  const promise = _startWebContainerPreviewInner(options, sessionId, onLog);
  _startingPreview = promise;
  try {
    return await promise;
  } finally {
    if (_startingPreview === promise) _startingPreview = null;
  }
}

async function _startWebContainerPreviewInner(
  options: BundleOptions,
  sessionId: string,
  onLog?: (line: string) => void,
): Promise<WebContainerPreviewResult> {
  let container: Awaited<ReturnType<typeof getContainer>>;
  try {
    container = await getContainer();
    // Probe the container to detect "Process aborted" state
    await container.fs.readdir('/');
  } catch (probeErr) {
    // Container is dead — reset and reboot
    console.warn('[webcontainer] Container in bad state, rebooting:', probeErr);
    _container = null;
    _booting = null;
    _serverUrl = null;
    _currentSessionId = null;
    _installedDepsHash = null;
    _hasBootstrapped = false;
    _devProcess = null;
    container = await getContainer();
  }
  const framework = options.framework || 'react';
  const errors: string[] = [];

  // Build the file tree (always includes our vite.config with resolve.extensions)
  const fileTree = buildFileTree(options.files, framework);

  // Compute a hash of package.json deps to detect changes across sessions
  const pkgJsonContents = (fileTree['package.json'] as any)?.file?.contents || '{}';
  let depsHash: string;
  try {
    const pkg = JSON.parse(pkgJsonContents);
    depsHash = JSON.stringify({ d: pkg.dependencies, dd: pkg.devDependencies });
  } catch {
    depsHash = pkgJsonContents;
  }

  // Helper: run npm install and return success/failure
  const runInstall = async (): Promise<boolean> => {
    onLog?.('[preview] Installing dependencies...');
    const installProcess = await container.spawn('npm', ['install', '--legacy-peer-deps']);
    // Capture install output for debugging failures
    installProcess.output.pipeTo(
      new WritableStream({
        write(chunk) {
          const text = String(chunk).trim();
          if (text) onLog?.(`[npm] ${text}`);
        },
      }),
    ).catch(() => {});
    const installCode = await installProcess.exit;
    if (installCode !== 0) {
      onLog?.('[preview] npm install failed (exit code: ' + installCode + ')');
      return false;
    }
    return true;
  };

  // If same session and server is running, just update changed files (HMR)
  if (_currentSessionId === sessionId && _serverUrl) {
    // Only rewrite config files if they've changed — avoids unnecessary Vite restarts
    // which cause "server is being restarted or closed" race conditions during dep-scan
    const newViteConfig = (fileTree['vite.config.mts'] as any).file.contents;
    const newPkgJson = (fileTree['package.json'] as any).file.contents;

    // Check vite config
    try {
      const currentViteConfig = await container.fs.readFile('/vite.config.mts', 'utf-8');
      if (currentViteConfig !== newViteConfig) {
        try { await container.fs.rm('/vite.config.ts'); } catch {}
        try { await container.fs.rm('/vite.config.js'); } catch {}
        await container.fs.writeFile('/vite.config.mts', newViteConfig);
      }
    } catch {
      try { await container.fs.rm('/vite.config.ts'); } catch {}
      try { await container.fs.rm('/vite.config.js'); } catch {}
      await container.fs.writeFile('/vite.config.mts', newViteConfig);
    }

    // Check package.json — compare deps before writing to know if npm install is needed
    let needsInstall = false;
    try {
      const currentPkgJson = await container.fs.readFile('/package.json', 'utf-8');
      if (currentPkgJson !== newPkgJson) {
        // Check if actual deps changed (not just formatting)
        try {
          const oldPkg = JSON.parse(currentPkgJson);
          const newPkg = JSON.parse(newPkgJson);
          const oldDeps = JSON.stringify({ d: oldPkg.dependencies, dd: oldPkg.devDependencies });
          const newDeps = JSON.stringify({ d: newPkg.dependencies, dd: newPkg.devDependencies });
          needsInstall = oldDeps !== newDeps;
        } catch {
          needsInstall = true;
        }
        await container.fs.writeFile('/package.json', newPkgJson);
      }
    } catch {
      await container.fs.writeFile('/package.json', newPkgJson);
      needsInstall = true;
    }

    await writeFilesIncremental(container, options.files);

    // Re-inject theme CSS after file writes (AI may have overwritten index.html)
    if (_lastThemeCss) {
      try { await injectThemeIntoWebContainer(_lastThemeCss); } catch {}
    }

    // Run npm install if dependencies changed
    if (needsInstall) {
      onLog?.('[preview] New dependencies detected — running npm install...');
      await runInstall();
    }

    return { url: _serverUrl, errors: [] };
  }

  // If we've previously bootstrapped, preserve node_modules by writing files individually
  // instead of using mount() which wipes the entire filesystem including node_modules.
  if (_hasBootstrapped) {
    onLog?.('[preview] Switching session — preserving cached node_modules...');
    // Kill the old Vite process before starting a new one
    if (_devProcess) {
      try { _devProcess.kill(); } catch {}
      _devProcess = null;
    }
    _serverUrl = null;
    _currentSessionId = sessionId;

    // Clean up any stale vite config variants before writing managed one
    for (const staleConfig of ['vite.config.ts', 'vite.config.js', 'vite.config.mjs']) {
      try { await container.fs.rm('/' + staleConfig); } catch {}
    }

    // Write all source files individually (skips node_modules)
    await writeFileTree(container, fileTree, '/');

    // Only run npm install if dependencies changed
    if (_installedDepsHash !== depsHash) {
      onLog?.('[preview] Dependencies changed — running npm install...');
      const ok = await runInstall();
      if (ok) {
        _installedDepsHash = depsHash;
      } else {
        // npm install failed with cached node_modules — do a full remount
        onLog?.('[preview] Cached node_modules broken — doing full remount...');
        _installedDepsHash = null;
        await container.mount(fileTree);
        const retryOk = await runInstall();
        if (retryOk) {
          _installedDepsHash = depsHash;
        } else {
          errors.push('npm install failed');
        }
      }
    } else {
      onLog?.('[preview] Dependencies unchanged — skipping npm install');
    }
  } else {
    // First-ever mount — use mount() for speed
    _currentSessionId = sessionId;
    _hasBootstrapped = true;
    await container.mount(fileTree);

    const ok = await runInstall();
    if (ok) {
      _installedDepsHash = depsHash;
    } else {
      errors.push('npm install failed');
    }
  }

  // Don't start Vite if npm install failed — it'll just spin forever
  if (errors.includes('npm install failed')) {
    onLog?.('[preview] Skipping Vite — npm install failed');
    return { url: '', errors };
  }

  // Kill any previous Vite process to prevent rogue spinners
  if (_devProcess) {
    try { _devProcess.kill(); } catch {}
    _devProcess = null;
  }

  // Register server-ready listener BEFORE spawning to avoid race condition
  onLog?.('[preview] Starting Vite dev server...');
  const serverReadyPromise = new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Vite dev server timeout (60s)')), 60000);

    // Use a one-time listener pattern to avoid stacking handlers
    const handler = (_port: number, serverUrl: string) => {
      clearTimeout(timeout);
      resolve(serverUrl);
    };
    container.on('server-ready', handler);
  });
  // Swallow unhandled rejection on timeout (moved outside constructor to avoid TDZ)
  serverReadyPromise.catch(() => {});

  const devProcess = await container.spawn('npx', ['--yes', 'vite', '--host', '0.0.0.0']);
  _devProcess = devProcess;

  // Capture output for debugging and error detection
  devProcess.output.pipeTo(
    new WritableStream({
      write(chunk) {
        onLog?.(`[vite] ${chunk}`);
        // Detect Vite transform/compile errors in output and fire callback
        const text = String(chunk);
        if (
          /(?:error|ERROR|SyntaxError|TypeError|ReferenceError|Failed to resolve|Internal server error|Transform failed)/i.test(text) &&
          !/hmr|update|ready|optimized/i.test(text)
        ) {
          _onViteError?.(text);
        }
      },
    }),
  );

  // Wait for the server-ready event
  const url = await serverReadyPromise;

  _serverUrl = url;
  onLog?.(`[preview] Vite ready at ${url}`);

  return { url, errors };
}

/** Recursively write a FileSystemTree to the container without using mount() (preserves node_modules). */
async function writeFileTree(
  container: WebContainer,
  tree: FileSystemTree,
  basePath: string,
): Promise<void> {
  for (const [name, entry] of Object.entries(tree)) {
    // Skip node_modules — we want to preserve the cached copy
    if (name === 'node_modules') continue;
    const fullPath = basePath === '/' ? `/${name}` : `${basePath}/${name}`;
    if ('directory' in entry) {
      try {
        await container.fs.mkdir(fullPath, { recursive: true });
      } catch {
        // Already exists
      }
      await writeFileTree(container, entry.directory, fullPath);
    } else if ('file' in entry) {
      // Ensure parent directory exists
      const dir = fullPath.substring(0, fullPath.lastIndexOf('/'));
      if (dir) {
        try {
          await container.fs.mkdir(dir, { recursive: true });
        } catch {
          // Already exists
        }
      }
      await container.fs.writeFile(fullPath, entry.file.contents);
    }
  }
}

/** Write only changed files (for HMR updates). */
async function writeFilesIncremental(
  container: WebContainer,
  files: BundleOptions['files'],
): Promise<void> {
  // Pre-collect all paths to detect conflicts (e.g., src/App.tsx as file AND directory)
  const allFilePaths = new Set<string>();
  for (const f of files) {
    if (!f.content) continue;
    const fp = f.filePath && f.filePath !== '/'
      ? `${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`
      : f.fileName;
    allFilePaths.add(fp);
  }

  for (const f of files) {
    if (!f.content) continue;

    // Never write sensitive files to the preview container
    if (/^\.env($|\.)/.test(f.fileName)) continue;

    // Skip non-source files that trigger unnecessary Vite reloads/restarts
    if (/\.sql$/.test(f.fileName)) continue;
    if (f.fileName === 'components.json' || f.fileName === 'eslint.config.js') continue;
    if (/^tsconfig(\.\w+)?\.json$/.test(f.fileName) && f.filePath === '/') continue;
    // Skip supabase config/migration files
    if (f.filePath?.includes('supabase/')) continue;

    // Skip config files that we manage ourselves (vite.config, postcss.config, svelte.config)
    if (/^(vite\.config\.(ts|js|mts|mjs)|postcss\.config\.(js|cjs|mjs|ts)|svelte\.config\.(js|ts))$/.test(f.fileName)) continue;

    // Skip SvelteKit-specific files that break plain Svelte (app.html, +page.svelte, +layout.svelte)
    if (f.fileName === 'app.html' && f.filePath?.includes('src')) continue;
    if (f.fileName.startsWith('+') && f.fileName.endsWith('.svelte')) continue;

    const fullPath = f.filePath && f.filePath !== '/'
      ? `/${f.filePath.replace(/^\//, '').replace(/\/$/, '')}/${f.fileName}`
      : `/${f.fileName}`;

    // Skip files whose path treats an existing file as a directory
    // e.g., skip "/src/App.tsx/App.tsx" when "/src/App.tsx" is a real file
    const segments = fullPath.replace(/^\//, '').split('/');
    let skipFile = false;
    for (let i = 1; i < segments.length; i++) {
      const parentPath = segments.slice(0, i).join('/');
      if (allFilePaths.has(parentPath)) {
        skipFile = true;
        break;
      }
    }
    if (skipFile) continue;

    // Ensure parent directory exists
    const dir = fullPath.substring(0, fullPath.lastIndexOf('/'));
    if (dir) {
      try {
        await container.fs.mkdir(dir, { recursive: true });
      } catch {
        // Already exists
      }
    }

    // Sanitize JS/TS imports
    let content = f.content;
    if (/\.(tsx?|jsx?|mjs)$/.test(f.fileName)) {
      content = stripConflictingStubs(content);
      content = deduplicateImports(content, fullPath);
    }

    // CSS files are left as-is — Tailwind v4 + PostCSS handles @import "tailwindcss" natively

    // Fix tsconfig.json — remove broken extends and enforce safe defaults
    if (f.fileName === 'tsconfig.json') {
      try {
        const parsed = JSON.parse(content);
        if (parsed.extends) delete parsed.extends;
        parsed.compilerOptions = parsed.compilerOptions || {};
        parsed.compilerOptions.jsx = parsed.compilerOptions.jsx || 'react-jsx';
        parsed.compilerOptions.skipLibCheck = true;
        parsed.compilerOptions.strict = false;
        parsed.compilerOptions.noImplicitAny = false;
        parsed.compilerOptions.noUnusedLocals = false;
        parsed.compilerOptions.noUnusedParameters = false;
        parsed.compilerOptions.moduleResolution = parsed.compilerOptions.moduleResolution || 'bundler';
        if (!parsed.compilerOptions.paths) parsed.compilerOptions.paths = {};
        if (!parsed.compilerOptions.paths['@/*']) parsed.compilerOptions.paths['@/*'] = ['./src/*'];
        content = JSON.stringify(parsed, null, 2);
      } catch {
        // Parse failed — skip, buildFileTree already handles this
      }
    }

    // Binary files stored as data URLs need to be decoded to Uint8Array
    // so the WebContainer filesystem serves actual binary content (e.g., GLB for Three.js)
    const dataUrlMatch = content.match(/^data:[^;]*;base64,(.+)$/);
    if (dataUrlMatch && /\.(glb|gltf|png|jpg|jpeg|gif|webp|ico|woff2?|ttf|otf|mp3|wav|ogg|mp4|webm|pdf|wasm|pck|fbx|obj|hdr|exr)$/i.test(f.fileName)) {
      try {
        const raw = atob(dataUrlMatch[1]);
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        await container.fs.writeFile(fullPath, bytes);
      } catch {
        await container.fs.writeFile(fullPath, content);
      }
    } else {
      await container.fs.writeFile(fullPath, content);
    }
  }
}

/** Tear down the WebContainer (call on unmount). */
export async function teardownWebContainer(): Promise<void> {
  if (_devProcess) {
    try { _devProcess.kill(); } catch {}
    _devProcess = null;
  }
  if (_container) {
    try {
      _container.teardown();
    } catch {
      // Already torn down or not initialized
    }
    _container = null;
    _serverUrl = null;
    _currentSessionId = null;
    _booting = null;
    _installedDepsHash = null;
    _hasBootstrapped = false;
  }
}

/**
 * Write theme CSS variables into the running WebContainer so the preview
 * picks them up via Vite HMR. The CSS is written to /tasking-theme-vars.css
 * and auto-imported into the index.html <head>.
 */
let _lastThemeCss: string | null = null;

/**
 * Run `vite build` inside the WebContainer and return the dist/ files
 * as an array of { path, content } ready for publishing.
 */
export async function buildForPublish(
  onLog?: (line: string) => void,
): Promise<{ files: { path: string; content: string }[]; errors: string[] }> {
  if (!_container) {
    return { files: [], errors: ['WebContainer not running — open the live preview first.'] };
  }

  const errors: string[] = [];
  onLog?.('[publish-build] Preparing production build...');

  // Strip ALL Tailwind/PostCSS so vite build doesn't try to use WASM.
  // We inject Tailwind CDN into the built HTML instead.

  // 1. Nuke PostCSS configs
  try { await _container.fs.writeFile('/postcss.config.js', 'export default { plugins: {} };\n'); } catch {}
  try { await _container.fs.rm('/postcss.config.cjs'); } catch {}
  try { await _container.fs.rm('/postcss.config.mjs'); } catch {}
  onLog?.('[publish-build] Disabled PostCSS');

  // 2. Strip Tailwind directives from ALL CSS files (not just known paths)
  async function cleanCssInDir(dir: string) {
    try {
      const entries = await _container!.fs.readdir(dir, { withFileTypes: true });
      for (const entry of entries) {
        const full = dir === '/' ? `/${entry.name}` : `${dir}/${entry.name}`;
        if (entry.name === 'node_modules' || entry.name === 'dist') continue;
        if (entry.isDirectory()) {
          await cleanCssInDir(full);
        } else if (/\.css$/i.test(entry.name)) {
          try {
            const css = await _container!.fs.readFile(full, 'utf-8');
            if (/@import\s+["']tailwindcss|@tailwind\s|@source\s/.test(css)) {
              const cleaned = css
                .replace(/@import\s+["']tailwindcss["'][^;]*;?/gm, '/* tw CDN */')
                .replace(/@tailwind\s+\w+\s*;/gm, '/* tw CDN */')
                .replace(/@source\s+[^;]*;?/gm, '/* tw CDN */')
                .replace(/@custom-variant\s+[^;]*;?/gm, '/* tw CDN */')
                .replace(/@theme\s+inline\s*\{[\s\S]*?\}/gm, '/* tw CDN */');
              await _container!.fs.writeFile(full, cleaned);
              onLog?.(`[publish-build] Cleaned ${full}`);
            }
          } catch {}
        }
      }
    } catch {}
  }
  await cleanCssInDir('/');

  // 3. Strip Tailwind-specific blocks from index.html
  // ONLY remove whole <script>/<style> blocks — never regex inside them
  // (the Tailwind config script has CSS directives as JS strings that corrupt if regex'd)
  try {
    let html = await _container.fs.readFile('/index.html', 'utf-8');
    let changed = false;
    // Remove <script data-tasking-tw-config>...</script> (Tailwind CDN config injected by preview)
    if (html.includes('data-tasking-tw-config')) {
      html = html.replace(/<script[^>]*data-tasking-tw-config[^>]*>[\s\S]*?<\/script>\s*/gi, '');
      changed = true;
    }
    // Remove <style type="text/tailwindcss">...</style> blocks
    if (html.includes('text/tailwindcss')) {
      html = html.replace(/<style[^>]*type=["']text\/tailwindcss["'][^>]*>[\s\S]*?<\/style>\s*/gi, '');
      changed = true;
    }
    // Remove the Tailwind CDN <script> tag (build handles CSS, CDN re-added after build)
    if (html.includes('@tailwindcss/browser')) {
      html = html.replace(/<script[^>]*src=["'][^"']*@tailwindcss\/browser[^"']*["'][^>]*><\/script>\s*/gi, '');
      changed = true;
    }
    if (changed) {
      await _container.fs.writeFile('/index.html', html);
      onLog?.('[publish-build] Cleaned Tailwind blocks from index.html');
    }
  } catch {}

  onLog?.('[publish-build] Running vite build...');

  // Run vite build
  const buildProcess = await _container.spawn('npx', ['--yes', 'vite', 'build']);
  let buildOutput = '';

  await buildProcess.output.pipeTo(
    new WritableStream({
      write(chunk) {
        buildOutput += chunk;
        onLog?.(`[vite-build] ${chunk}`);
      },
    }),
  );

  const exitCode = await buildProcess.exit;

  // Restore PostCSS config so the dev server keeps working
  try {
    await _container.fs.writeFile('/postcss.config.js',
      `export default {\n  plugins: {\n    '@tailwindcss/postcss': {},\n  },\n};\n`);
  } catch {}

  if (exitCode !== 0) {
    errors.push(`vite build failed (exit code ${exitCode}): ${buildOutput.slice(-500)}`);
    return { files: [], errors };
  }

  onLog?.('[publish-build] Build complete, reading dist/ files...');

  // Recursively read all files from dist/
  const files: { path: string; content: string }[] = [];

  async function readDir(dirPath: string, prefix: string) {
    try {
      const entries = await _container!.fs.readdir(dirPath);
      onLog?.(`[publish-build] readDir ${dirPath}: ${entries.length} entries`);
      for (const name of entries) {
        const fullPath = `${dirPath}/${name}`;
        const publishPath = prefix ? `${prefix}/${name}` : name;

        // If it has a file extension, read as file; otherwise try as directory first
        const hasExt = /\.\w{1,10}$/.test(name);

        if (hasExt) {
          // Likely a file
          try {
            const content = await _container!.fs.readFile(fullPath, 'utf-8');
            if (content.length > 0) {
              files.push({ path: publishPath, content });
              onLog?.(`[publish-build]   file: ${publishPath} (${content.length} bytes)`);
            } else {
              onLog?.(`[publish-build]   skip: ${publishPath} (empty)`);
            }
          } catch {
            onLog?.(`[publish-build]   skip: ${publishPath} (binary or unreadable)`);
          }
        } else {
          // Likely a directory — recurse
          try {
            await readDir(fullPath, publishPath);
          } catch {
            // Maybe it's an extensionless file
            try {
              const content = await _container!.fs.readFile(fullPath, 'utf-8');
              if (content.length > 0) {
                files.push({ path: publishPath, content });
                onLog?.(`[publish-build]   file: ${publishPath} (${content.length} bytes)`);
              }
            } catch {}
          }
        }
      }
    } catch (err: any) {
      onLog?.(`[publish-build] readDir FAILED: ${dirPath} — ${err.message}`);
      errors.push(`Failed to read ${dirPath}: ${err.message}`);
    }
  }

  await readDir('/dist', '');
  onLog?.(`[publish-build] Total files from dist/: ${files.length}`);

  if (files.length === 0) {
    errors.push('vite build produced no output files in dist/');
  }

  // Inject Tailwind CDN into the built index.html (since we stripped PostCSS)
  const indexFile = files.find(f => f.path === 'index.html');
  if (indexFile && !indexFile.content.includes('tailwindcss/browser')) {
    indexFile.content = indexFile.content.replace(
      '</head>',
      '  <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>\n</head>',
    );
    onLog?.('[publish-build] Injected Tailwind CDN into index.html');
  }

  onLog?.(`[publish-build] Collected ${files.length} files from dist/`);
  return { files, errors };
}

export async function injectThemeIntoWebContainer(themeCss: string): Promise<void> {
  _lastThemeCss = themeCss;
  if (!_container) return;
  const filePath = '/tasking-theme-vars.css';
  try {
    await _container.fs.writeFile(filePath, themeCss);
    // Inject a <link> into index.html if not already present + sanitize any
    // unclosed <script>/<style>/<title>/<textarea> the AI may have written.
    try {
      let html = await _container.fs.readFile('/index.html', 'utf-8');
      const repaired = sanitizeRawTextElements(html, '[injectTheme]');
      let dirty = repaired !== html;
      html = repaired;
      if (!html.includes('tasking-theme-vars.css')) {
        html = html.replace('</head>', `  <link rel="stylesheet" href="/tasking-theme-vars.css">\n</head>`);
        dirty = true;
      }
      if (dirty) {
        await _container.fs.writeFile('/index.html', html);
      }
    } catch { /* index.html might not exist */ }
  } catch (err) {
    console.debug('[webcontainer] Failed to inject theme CSS:', err);
  }
}
